import type { ComputedRef, Ref } from 'vue'
import type { TableSortEvent } from '@app/shared/vue/components/sf-table/types'
import type {
  SortableUserColumn,
  UserList,
  UserRow,
  UserSortDirection,
} from '@app/application/user-management/types'

import { computed, ref, watch } from 'vue'

import {
  isSortableUserColumn,
  userColumns,
  userSortDirections,
} from '@app/application/user-management/types'
import {
  FIRST_PAGE,
  QUERY_KEYS,
  STALE_USERS_REQUEST_REASON,
  USERS_REQUEST_KEY,
} from '@app/application/user-management/constants'
import { abort, getSignal } from '@app/adapters/abort'
import { fetchUsers } from '@app/application/user-management/api'
import { SfLog } from '@app/adapters/logger'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'

export interface UsersListState {
  users: Ref<UserRow[]>
  totalPages: Ref<number>
  loading: Ref<boolean>
  error: Ref<boolean>
  sortBy: ComputedRef<SortableUserColumn>
  sortDirection: ComputedRef<UserSortDirection>
  page: ComputedRef<number>
  setSort: (sort: TableSortEvent) => void
  setPage: (page: number) => void
}

type QueryParam = string | (string | null)[]

const toSortBy = (param: QueryParam): SortableUserColumn =>
  typeof param === 'string' && isSortableUserColumn(param)
    ? param
    : userColumns.NAME

const toSortDirection = (param: QueryParam): UserSortDirection =>
  param === userSortDirections.DESC
    ? userSortDirections.DESC
    : userSortDirections.ASC

const toPage = (param: QueryParam): number => {
  const page = Number(param)

  return Number.isInteger(page) && page > 0 ? page : FIRST_PAGE
}

export const useUsersList = ({ list }: { list: UserList }): UsersListState => {
  const sortBy = useUrlStorage<SortableUserColumn>(
    QUERY_KEYS.SORT_BY,
    userColumns.NAME,
    String,
    { deserialize: toSortBy }
  )
  const sortDirection = useUrlStorage<UserSortDirection>(
    QUERY_KEYS.SORT_DIRECTION,
    userSortDirections.ASC,
    String,
    { deserialize: toSortDirection }
  )
  const page = useUrlStorage<number>(QUERY_KEYS.PAGE, FIRST_PAGE, String, {
    deserialize: toPage,
  })

  const users = ref<UserRow[]>([])
  const totalPages = ref(FIRST_PAGE)
  const loading = ref(true)
  const error = ref(false)

  const setSort = (sort: TableSortEvent): void => {
    if (!isSortableUserColumn(sort.sortBy)) {
      return
    }

    sortBy.value = sort.sortBy
    sortDirection.value = toSortDirection(sort.sortDirection)
    // The rows the old page held are not the rows this sort puts there.
    page.value = FIRST_PAGE
  }

  const setPage = (next: number): void => {
    page.value = next
  }

  const loadUsers = async (): Promise<void> => {
    abort(USERS_REQUEST_KEY, STALE_USERS_REQUEST_REASON)

    // Two clicks in a row leave two requests in flight and they can answer in
    // either order. The abort above cancels the older one, and its signal is
    // how that run tells the table has moved on without it.
    const signal = getSignal(USERS_REQUEST_KEY)
    const isStale = (): boolean => signal?.aborted === true

    loading.value = true
    error.value = false

    try {
      const response = await fetchUsers({
        list,
        page: page.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
      })

      if (isStale()) {
        return
      }

      users.value = response?.users ?? []
      totalPages.value = response?.pagination?.totalPages ?? FIRST_PAGE

      // A bookmarked page past the end answers with no rows and the page it was
      // asked for, so the list holds on to the last page it does have.
      if (page.value > totalPages.value) {
        setPage(totalPages.value)
      }
    } catch (e) {
      if (isStale()) {
        return
      }

      SfLog.warn('Failed to load the users list', { extra: { error: e } })

      // The page count stays as it was, so the pager is still there to retry with.
      error.value = true
    } finally {
      if (!isStale()) {
        loading.value = false
      }
    }
  }

  watch([sortBy, sortDirection, page], loadUsers, { immediate: true })

  return {
    users,
    totalPages,
    loading,
    error,
    sortBy: computed(() => sortBy.value),
    sortDirection: computed(() => sortDirection.value),
    page: computed(() => page.value),
    setSort,
    setPage,
  }
}
